import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import sendolaLogo from "@/assets/Img/logo-sendola-2026-white.png";

const sendolaPayUrl = import.meta.env.VITE_SENDOLA_PAY_URL;

export const HomeownersFooter = () => {
  const [t] = useTranslation("homeowners");
  const [tGlobal] = useTranslation("global");

  return (
    <footer className="w-full bg-[#171a1e] px-6 sm:px-12 lg:px-24 xl:px-32 py-16 lg:py-28">
      <div className="flex flex-col gap-12 lg:gap-14">
        {/* Top row — company name + logo */}
        <div className="flex items-end justify-between">
          <p className="font-montserrat font-bold text-white text-[clamp(1rem,0.8rem+0.6vw,1.5rem)]">
            {tGlobal("Footer.Address1")}
          </p>
          <a
            href="/"
            className="h-10 lg:h-14 flex-shrink-0 hover:opacity-80 transition-opacity"
          >
            <img
              src={sendolaLogo}
              alt="Sendola logo"
              className="h-full w-auto object-contain block"
            />
          </a>
        </div>

        {/* Main columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Column 1 — Address + Contact */}
          <div className="flex flex-col gap-6 font-montserrat font-medium text-white text-[clamp(0.75rem,0.6rem+0.4vw,1.125rem)] leading-relaxed">
            <div>
              <p>{tGlobal("Footer.Address2")}</p>
              <p>{tGlobal("Footer.Address3")}</p>
            </div>
            <div className="flex flex-col gap-2.5">
              <p>
                {tGlobal("Footer.Phone")} {tGlobal("Footer.PhoneNumber")}
              </p>
              <p>
                {tGlobal("Footer.ReportIssue")}{" "}
                <a
                  href={`mailto:${tGlobal("Footer.SupportEmail")}`}
                  className="underline hover:opacity-80 transition-opacity"
                >
                  {tGlobal("Footer.SupportEmail")}
                </a>
              </p>
            </div>
          </div>

          {/* Column 2 — Solutions */}
          <div className="flex flex-col gap-4 font-montserrat text-white text-[clamp(0.75rem,0.6rem+0.4vw,1.125rem)] leading-relaxed">
            <p className="font-bold">{t("Footer.Solutions")}</p>
            <a href="/" className="font-medium hover:opacity-80 transition-opacity">
              {t("Footer.MakePayments")}
            </a>
            <a href={sendolaPayUrl} target="_blank" rel="noreferrer" className="font-medium hover:opacity-80 transition-opacity">
              {t("Footer.SendMoney")}
            </a>
          </div>

          {/* Column 3 — Page links */}
          <div className="flex flex-col gap-4 font-montserrat font-medium text-white text-[clamp(0.75rem,0.6rem+0.4vw,1.125rem)] leading-relaxed">
            <a href="#pricing-savings" className="hover:opacity-80 transition-opacity">
              {t("Footer.Pricing")}
            </a>
            <a href="#who-we-pay" className="hover:opacity-80 transition-opacity">
              {t("Footer.WhoWePay")}
            </a>
            <a href="#who-we-serve" className="hover:opacity-80 transition-opacity">
              {t("Footer.UseCases")}
            </a>
            <a href="#concierge-service" className="hover:opacity-80 transition-opacity">
              {t("Footer.ConciergeService")}
            </a>
            <a href="#frequent-qa" className="hover:opacity-80 transition-opacity">
              {t("Footer.Faqs")}
            </a>
          </div>

          {/* Column 4 — Legal + LinkedIn */}
          <div className="flex flex-col gap-4 items-start">
            <Link
              to="/legal"
              className="font-montserrat font-medium text-white text-[clamp(0.75rem,0.6rem+0.4vw,1.125rem)] hover:opacity-80 transition-opacity"
            >
              {t("Footer.LegalInformation")}
            </Link>
            <a
              href="https://www.linkedin.com/company/sendola-payments/"
              target="_blank"
              rel="noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                fill="currentColor"
                className="w-9 h-9 text-white"
                aria-label="LinkedIn"
              >
                <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom — NMLS + Disclaimer */}
        <div className="flex flex-col gap-6 lg:gap-8">
          <p className="font-montserrat font-bold text-white text-[clamp(1rem,0.8rem+0.6vw,1.5rem)]">
            {tGlobal("Footer.SendolaNMLS")}
          </p>
          <div className="flex flex-col gap-4 font-montserrat font-medium text-white text-[clamp(0.7rem,0.55rem+0.35vw,1rem)] leading-relaxed">
            <p
              dangerouslySetInnerHTML={{
                __html: tGlobal("Footer.Disclaimer"),
              }}
            />
            <p>{tGlobal("Footer.Disclaimer3")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
